/* ==========================================================================
   Umbría — JavaScript del tema (vanilla, sin dependencias)
   Módulos: utilidades · API del carrito · cajón del carrito · cabecera ·
   anuncios · galería + lightbox · página de producto (ofertas, variantes,
   formulario) · barra fija · packs · carruseles · acordeones · animaciones
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const strings = theme.strings || {};
  const routes = theme.routes || {};

  /* ---------------------------------------------------------------------- */
  /* Utilidades                                                              */
  /* ---------------------------------------------------------------------- */

  /** Formato de dinero de Shopify (respeta el formato de la tienda, p. ej. «{{amount_with_comma_separator}} €»). */
  function formatMoney(cents, format = theme.moneyFormat || '{{amount_with_comma_separator}} €') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (n, precision, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(precision);
      const [int, dec] = fixed.split('.');
      const intFmt = int.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return dec ? intFmt + decimal + dec : intFmt;
    };
    const match = format.match(placeholder);
    if (!match) return fmt(cents, 2, '.', ',') + ' €';
    let value;
    switch (match[1]) {
      case 'amount': value = fmt(cents, 2); break;
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      default: value = fmt(cents, 2, '.', ',');
    }
    return format.replace(placeholder, value);
  }

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function toast(message, ms = 3200) {
    const el = qs('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, ms);
  }

  /** Bloqueo de scroll y trampa de foco para paneles modales. */
  const focusable = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    const items = qsa(focusable, container).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
  let lockCount = 0;
  const lockScroll = () => { lockCount += 1; document.body.classList.add('is-locked'); };
  const unlockScroll = () => {
    lockCount = Math.max(0, lockCount - 1);
    // Solo se libera si no queda ningún panel modal abierto.
    if (!lockCount || !document.querySelector('.drawer.is-open, .mobile-nav.is-open, dialog[open]')) {
      lockCount = 0;
      document.body.classList.remove('is-locked');
    }
  };

  /* ---------------------------------------------------------------------- */
  /* API del carrito (endpoints AJAX nativos de Shopify)                    */
  /* ---------------------------------------------------------------------- */

  const CART_SECTION = 'cart-drawer';

  async function request(url, body) {
    const res = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body ? JSON.stringify(body) : undefined,
    });
    let data = null;
    try { data = await res.json(); } catch (e) { /* respuesta vacía */ }
    if (!res.ok || (data && data.status && data.status >= 400)) {
      const msg = (data && (data.description || data.message)) || strings.genericError;
      const err = new Error(typeof msg === 'string' ? msg : strings.genericError);
      err.data = data;
      throw err;
    }
    return data;
  }

  const CartAPI = {
    add(items) {
      return request(routes.cartAdd, { items, sections: CART_SECTION, sections_url: window.location.pathname });
    },
    change(line, quantity) {
      return request(routes.cartChange, { line, quantity, sections: CART_SECTION, sections_url: window.location.pathname });
    },
    get() { return request(routes.cartJs); },
  };

  function setCartCount(count) {
    qsa('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = !count;
      el.classList.remove('is-bumped');
      void el.offsetWidth; // reinicia la animación
      el.classList.add('is-bumped');
    });
  }

  /** Tras añadir: abre el cajón (o va a /cart si el tema usa página de carrito). */
  async function afterAdd(response) {
    const drawer = qs('cart-drawer');
    if (theme.cartType === 'drawer' && drawer && response && response.sections && response.sections[CART_SECTION]) {
      drawer.renderFromHTML(response.sections[CART_SECTION]);
      drawer.open();
    } else {
      window.location.href = routes.cart;
    }
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  /* ---------------------------------------------------------------------- */
  /* Cajón del carrito                                                       */
  /* ---------------------------------------------------------------------- */

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-cart-toggle]');
        if (toggle && theme.cartType === 'drawer') { e.preventDefault(); this.open(toggle); }
      });
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.opener = opener || document.activeElement;
      this.hidden = false;
      void this.offsetWidth; // fuerza el reflow para que la transición se ejecute
      this.classList.add('is-open');
      lockScroll();
      document.addEventListener('keydown', this.onKeydown);
      setTimeout(() => qs('.drawer__panel', this)?.focus({ preventScroll: true }), 50);
      document.dispatchEvent(new CustomEvent('drawer:open'));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKeydown);
      unlockScroll();
      setTimeout(() => { this.hidden = true; }, 350);
      this.opener?.focus?.();
      document.dispatchEvent(new CustomEvent('drawer:close'));
    }

    onKeydown(e) {
      if (e.key === 'Escape') this.close();
      trapFocus(qs('.drawer__panel', this), e);
    }

    onClick(e) {
      if (e.target.closest('[data-cart-close]')) {
        const link = e.target.closest('a[href]');
        if (!link) e.preventDefault();
        this.close();
        return;
      }
      const btn = e.target.closest('[data-cart-qty]');
      if (btn) {
        e.preventDefault();
        this.update(Number(btn.dataset.line), Number(btn.dataset.cartQty));
      }
    }

    onChange(e) {
      const input = e.target.closest('[data-cart-input]');
      if (!input) return;
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      this.update(Number(input.dataset.line), qty);
    }

    async update(line, quantity) {
      if (this.busy) return;
      this.busy = true;
      this.classList.add('is-loading');
      this.setError('');
      try {
        const cart = await CartAPI.change(line, quantity);
        if (cart.sections && cart.sections[CART_SECTION]) this.renderFromHTML(cart.sections[CART_SECTION]);
        setCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      } catch (err) {
        // Re-sincroniza con el estado real del servidor y después muestra el error.
        await this.refresh();
        this.setError(err.message);
      } finally {
        this.busy = false;
        this.classList.remove('is-loading');
      }
    }

    async refresh() {
      try {
        const res = await fetch(`${routes.root.replace(/\/$/, '')}/?sections=${CART_SECTION}`);
        const data = await res.json();
        if (data[CART_SECTION]) this.renderFromHTML(data[CART_SECTION]);
      } catch (e) { /* sin conexión: se mantiene el estado */ }
    }

    renderFromHTML(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cart-drawer');
      if (!fresh) return;
      const wasOpen = this.classList.contains('is-open');
      const focusedLine = document.activeElement?.closest?.('[data-line]')?.dataset.line;
      this.innerHTML = fresh.innerHTML;
      this.dataset.itemCount = fresh.dataset.itemCount;
      setCartCount(Number(fresh.dataset.itemCount || 0));
      if (wasOpen) {
        const target = (focusedLine && qs(`[data-cart-input][data-line="${focusedLine}"]`, this)) || qs('.drawer__panel', this);
        target?.focus({ preventScroll: true });
      }
    }

    setError(msg) {
      const el = qs('[data-cart-error]', this);
      if (!el) return;
      el.textContent = msg;
      el.hidden = !msg;
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  // Mantiene el contador correcto al volver con el botón «atrás» (bfcache).
  window.addEventListener('pageshow', async (e) => {
    if (!e.persisted) return;
    try {
      const cart = await CartAPI.get();
      setCartCount(cart.item_count);
      qs('cart-drawer')?.refresh();
    } catch (err) { /* ignorar */ }
  });

  /* ---------------------------------------------------------------------- */
  /* Cabecera y menú móvil                                                   */
  /* ---------------------------------------------------------------------- */

  class HeaderComponent extends HTMLElement {
    connectedCallback() {
      this.nav = qs('.mobile-nav', this);
      this.panel = qs('.mobile-nav__panel', this);
      this.burger = qs('[data-menu-open]', this);
      this.onKeydown = (e) => { if (e.key === 'Escape') this.closeMenu(); else trapFocus(this.panel, e); };
      this.burger?.addEventListener('click', () => this.openMenu());
      qsa('[data-menu-close]', this).forEach((el) => el.addEventListener('click', () => this.closeMenu()));
      qsa('.mobile-nav a', this).forEach((a) => a.addEventListener('click', () => this.closeMenu()));
      // Cierra los desplegables de escritorio al hacer clic fuera.
      document.addEventListener('click', (e) => {
        qsa('.site-nav__details[open]', this).forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
      });
    }

    openMenu() {
      if (!this.nav) return;
      this.nav.hidden = false;
      void this.nav.offsetWidth;
      this.nav.classList.add('is-open');
      this.burger.setAttribute('aria-expanded', 'true');
      lockScroll();
      document.addEventListener('keydown', this.onKeydown);
      setTimeout(() => this.panel.focus({ preventScroll: true }), 50);
    }

    closeMenu() {
      if (!this.nav || this.nav.hidden) return;
      this.nav.classList.remove('is-open');
      this.burger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.onKeydown);
      unlockScroll();
      setTimeout(() => { this.nav.hidden = true; }, 350);
      this.burger.focus();
    }
  }
  customElements.define('header-component', HeaderComponent);

  /* ---------------------------------------------------------------------- */
  /* Anuncios rotativos                                                      */
  /* ---------------------------------------------------------------------- */

  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.slides = qsa('.announcement__slide', this);
      if (!this.slides.length) return;
      this.index = 0;
      this.slides[0].classList.add('is-active');
      this.slides.forEach((s, i) => { if (i) s.setAttribute('aria-hidden', 'true'); });
      if (this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const interval = Number(this.dataset.interval) || 4000;
      const start = () => { this.timer = setInterval(() => this.next(), interval); };
      const stop = () => clearInterval(this.timer);
      this.addEventListener('mouseenter', stop);
      this.addEventListener('mouseleave', start);
      this.addEventListener('focusin', stop);
      this.addEventListener('focusout', start);
      start();
    }
    disconnectedCallback() { clearInterval(this.timer); }
    next() { this.show((this.index + 1) % this.slides.length); }
    show(i) {
      this.slides[this.index].classList.remove('is-active');
      this.slides[this.index].setAttribute('aria-hidden', 'true');
      this.index = i;
      this.slides[i].classList.add('is-active');
      this.slides[i].removeAttribute('aria-hidden');
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ---------------------------------------------------------------------- */
  /* Galería de producto + lightbox con zoom                                 */
  /* ---------------------------------------------------------------------- */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-track]', this);
      this.slides = qsa('.gallery__slide', this);
      this.thumbs = qsa('[data-thumb]', this);
      this.prev = qs('[data-prev]', this);
      this.next = qs('[data-next]', this);
      this.counter = qs('[data-current]', this);
      this.lightbox = qs('[data-lightbox]', this);
      this.index = 0;

      this.prev?.addEventListener('click', () => this.go(this.index - 1));
      this.next?.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.thumb))));
      this.track?.addEventListener('scroll', () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.syncFromScroll());
      }, { passive: true });
      this.track?.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      });
      this.updateUI();
      if (this.lightbox) this.setupLightbox();
    }

    go(i, smooth = true) {
      if (!this.slides.length) return;
      const n = Math.max(0, Math.min(this.slides.length - 1, i));
      this.track.scrollTo({ left: this.slides[n].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      this.index = n;
      this.updateUI();
    }

    goToMedia(mediaId) {
      if (!mediaId) return;
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (slide) this.go(Number(slide.dataset.index));
    }

    syncFromScroll() {
      const w = this.track.clientWidth || 1;
      const i = Math.round(this.track.scrollLeft / w);
      if (i !== this.index) { this.index = i; this.updateUI(); }
    }

    updateUI() {
      if (this.counter) this.counter.textContent = this.index + 1;
      if (this.prev) this.prev.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index >= this.slides.length - 1;
      this.thumbs.forEach((t, i) => {
        const active = i === this.index;
        t.classList.toggle('is-active', active);
        if (active) {
          t.setAttribute('aria-current', 'true');
          t.parentElement.scrollTo({ left: t.offsetLeft - t.parentElement.clientWidth / 2 + t.clientWidth / 2, behavior: 'smooth' });
        } else t.removeAttribute('aria-current');
      });
    }

    /* Lightbox */
    setupLightbox() {
      this.lbItems = qsa('[data-lightbox-item]', this.lightbox);
      qsa('[data-lightbox-open]', this).forEach((btn) => btn.addEventListener('click', () => this.openLightbox(Number(btn.dataset.lightboxOpen))));
      qs('[data-lightbox-close]', this.lightbox)?.addEventListener('click', () => this.lightbox.close());
      qs('[data-lightbox-prev]', this.lightbox)?.addEventListener('click', () => this.lbStep(-1));
      qs('[data-lightbox-next]', this.lightbox)?.addEventListener('click', () => this.lbStep(1));
      this.lightbox.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.lbStep(1);
        if (e.key === 'ArrowLeft') this.lbStep(-1);
      });
      this.lightbox.addEventListener('close', () => { unlockScroll(); this.go(this.lbIndexToSlide(this.lbIndex), false); });
      this.lbItems.forEach((item) => {
        item.addEventListener('click', (e) => {
          const r = item.getBoundingClientRect();
          item.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
          item.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
          item.classList.toggle('is-zoomed');
        });
        item.addEventListener('mousemove', (e) => {
          if (!item.classList.contains('is-zoomed')) return;
          const r = item.getBoundingClientRect();
          item.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
          item.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
        });
      });
      // Swipe táctil
      let startX = null;
      const stage = qs('[data-lightbox-stage]', this.lightbox);
      stage.addEventListener('pointerdown', (e) => { startX = e.clientX; });
      stage.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        const zoomed = this.lbItems[this.lbIndex]?.classList.contains('is-zoomed');
        if (!zoomed && Math.abs(dx) > 50) this.lbStep(dx < 0 ? 1 : -1);
        startX = null;
      });
    }

    lbIndexToSlide(lbIndex) {
      const item = this.lbItems[lbIndex];
      return item ? Number(item.dataset.lightboxItem) : this.index;
    }

    openLightbox(slideIndex) {
      const lbIndex = Math.max(0, this.lbItems.findIndex((it) => Number(it.dataset.lightboxItem) === slideIndex));
      this.showLb(lbIndex);
      if (typeof this.lightbox.showModal === 'function') this.lightbox.showModal();
      else this.lightbox.setAttribute('open', '');
      lockScroll();
    }

    lbStep(d) { this.showLb((this.lbIndex + d + this.lbItems.length) % this.lbItems.length); }

    showLb(i) {
      this.lbItems.forEach((it, n) => {
        it.hidden = n !== i;
        it.classList.remove('is-zoomed');
        if (n === i) {
          const img = qs('img', it);
          if (img && !img.src && img.dataset.src) img.src = img.dataset.src;
        }
      });
      this.lbIndex = i;
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------------------------------------------------------------------- */
  /* Cálculo de ofertas con datos reales del producto                        */
  /* ---------------------------------------------------------------------- */

  function computeOffer(product, selectedVariant, offer) {
    const variant = (offer.variantPosition > 0 && product.variants[offer.variantPosition - 1]) || selectedVariant;
    const qty = Math.max(1, offer.qty || 1);
    const packs = offer.packs > 0 ? offer.packs : qty;
    let plan = null;
    let alloc = null;
    if (offer.type === 'subscription') {
      plan = product.plans.find((p) => !offer.planMatch || p.name.includes(offer.planMatch)) || null;
      alloc = plan ? variant.allocations.find((a) => a.plan === plan.id) || null : null;
    }
    let unit = variant.price;
    let unitCompare = Math.max(variant.price, variant.compare_at_price || 0);
    if (alloc) {
      unit = alloc.price;
      unitCompare = Math.max(unitCompare, alloc.compare_at_price || 0);
    }
    const total = unit * qty;
    const compareTotal = unitCompare * qty;
    const saving = Math.max(0, compareTotal - total);
    const available = Boolean(variant && variant.available) &&
      (offer.type === 'subscription' ? Boolean(alloc) : !variant.requires_selling_plan);
    return {
      variant, plan: alloc ? plan : null, qty, packs, total, compareTotal, saving,
      savePct: compareTotal > 0 ? Math.round((saving * 100) / compareTotal) : 0,
      perPack: Math.round(total / packs),
      perDay: offer.days > 0 ? Math.round(total / offer.days) : 0,
      available,
    };
  }

  function paintPrices(root, r) {
    const set = (sel, val) => qsa(sel, root).forEach((el) => { el.textContent = val; });
    set('[data-price]', formatMoney(r.total));
    set('[data-compare]', formatMoney(r.compareTotal));
    qsa('[data-compare]', root).forEach((el) => { el.hidden = r.saving <= 0; });
    set('[data-per-pack]', formatMoney(r.perPack));
    set('[data-save-pct]', r.savePct);
    qsa('[data-save]', root).forEach((el) => { el.hidden = r.savePct < 1; });
    set('[data-saving]', formatMoney(r.saving));
    qsa('[data-save-amount]', root).forEach((el) => { el.hidden = r.saving <= 0; });
    set('[data-per-day]', formatMoney(r.perDay));
    qsa('[data-per-day-row]', root).forEach((el) => { el.hidden = !r.perDay; });
  }

  const readJSON = (el) => { try { return JSON.parse(el.textContent); } catch (e) { return null; } };

  /* ---------------------------------------------------------------------- */
  /* Página de producto: variantes, selector de compra, formulario AJAX      */
  /* ---------------------------------------------------------------------- */

  class ProductPage extends HTMLElement {
    connectedCallback() {
      this.product = readJSON(qs('[data-product-json]', this));
      if (!this.product) return;
      this.form = qs('[data-product-form]', this) || qs(`#${this.dataset.formId}`);
      this.inputId = qs('[data-input-id]', this);
      this.inputPlan = qs('[data-input-plan]', this);
      this.inputQty = qs('[data-input-qty]', this);
      this.submit = qs('[data-submit]', this);
      this.multiplier = qs('[data-qty-multiplier]', this);
      this.gallery = qs('media-gallery', this);
      this.offerEls = qsa('.offer[data-offer]', this);
      this.offers = this.offerEls.map((el) => JSON.parse(el.dataset.offer));

      const params = new URLSearchParams(window.location.search);
      const fromUrl = this.product.variants.find((v) => String(v.id) === params.get('variant'));
      this.variant = fromUrl || this.product.variants.find((v) => v.available) || this.product.variants[0];

      const checked = this.offerEls.findIndex((el) => qs('.offer__radio', el)?.checked);
      this.selected = checked >= 0 ? checked : 0;

      this.addEventListener('change', (e) => this.onChange(e));
      this.form?.addEventListener('submit', (e) => this.onSubmit(e));
      this.render();
    }

    onChange(e) {
      if (e.target.matches('.offer__radio')) {
        this.selected = Math.max(0, this.offerEls.indexOf(e.target.closest('.offer')));
        this.render();
      } else if (e.target.matches('[data-option-index]')) {
        const options = qsa('[data-variant-picker] fieldset', this).map((fs) => qs('input:checked', fs)?.value);
        const v = this.product.variants.find((variant) => variant.options.every((o, i) => o === options[i]));
        qsa('[data-option-value]', this).forEach((el, i) => { el.textContent = options[i] || ''; });
        if (v) {
          this.variant = v;
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
          if (v.featured_media_id) this.gallery?.goToMedia(v.featured_media_id);
        } else {
          this.variant = null;
        }
        this.render();
      } else if (e.target.matches('[data-qty-multiplier]')) {
        this.render();
      }
    }

    get multiplierValue() {
      const n = parseInt(this.multiplier?.value, 10);
      return Number.isFinite(n) && n > 0 ? Math.min(n, 99) : 1;
    }

    /** Estado actual: id de variante, cantidad, plan y precios que se enviarán al carrito. */
    current() {
      if (!this.variant) return null;
      if (!this.offers.length) {
        const v = this.variant;
        return { variant: v, plan: null, qty: 1, total: v.price, compareTotal: Math.max(v.price, v.compare_at_price || 0), saving: 0, available: v.available && !v.requires_selling_plan, label: '' };
      }
      const offer = this.offers[this.selected] || this.offers[0];
      const r = computeOffer(this.product, this.variant, offer);
      r.label = qs('.offer__title', this.offerEls[this.selected])?.textContent.trim() || '';
      return r;
    }

    render() {
      // Recalcula cada tarjeta de oferta con la variante actual.
      this.offerEls.forEach((el, i) => {
        if (!this.variant) return;
        const r = computeOffer(this.product, this.variant, this.offers[i]);
        paintPrices(el, r);
        const radio = qs('.offer__radio', el);
        if (radio) {
          radio.disabled = !r.available;
          radio.dataset.variantId = r.variant.id;
          radio.dataset.sellingPlan = r.plan ? r.plan.id : '';
          radio.checked = i === this.selected;
        }
        el.classList.toggle('is-selected', i === this.selected);
        el.classList.toggle('is-unavailable', !r.available);
      });

      // Si la opción elegida ya no está disponible, salta a la primera disponible.
      const cur = this.current();
      if (cur && !cur.available && this.offers.length) {
        const firstOk = this.offers.findIndex((o) => computeOffer(this.product, this.variant, o).available);
        if (firstOk >= 0 && firstOk !== this.selected) { this.selected = firstOk; this.render(); return; }
      }

      const state = this.current();
      const mult = this.multiplierValue;
      const available = Boolean(state && state.available);

      if (state) {
        this.inputId.value = state.variant.id;
        this.inputQty.value = state.qty * mult;
        if (state.plan) { this.inputPlan.value = state.plan.id; this.inputPlan.disabled = false; }
        else { this.inputPlan.value = ''; this.inputPlan.disabled = true; }
      }

      const total = state ? state.total * mult : 0;
      const compareTotal = state ? state.compareTotal * mult : 0;
      const priceEl = qs('[data-submit-price]', this);
      const compareEl = qs('[data-submit-compare]', this);
      const labelEl = qs('[data-submit-label]', this);
      const defaultLabel = qs('[data-label-default]', this)?.value || strings.addToCart;
      if (priceEl) priceEl.textContent = state ? formatMoney(total) : '';
      if (compareEl) { compareEl.textContent = formatMoney(compareTotal); compareEl.hidden = !state || compareTotal <= total; }
      if (labelEl) labelEl.textContent = !state ? strings.unavailable : (available ? defaultLabel : strings.soldOut);
      qsa('.button__sep', this).forEach((el) => { el.hidden = !state; });
      if (this.submit) this.submit.disabled = !available;

      const mainPrice = qs('[data-main-price]', this);
      const mainCompare = qs('[data-main-compare]', this);
      if (mainPrice && this.variant) mainPrice.textContent = formatMoney(this.variant.price);
      if (mainCompare && this.variant) {
        mainCompare.textContent = formatMoney(this.variant.compare_at_price || 0);
        mainCompare.hidden = !(this.variant.compare_at_price > this.variant.price);
      }

      document.dispatchEvent(new CustomEvent('product:state', {
        detail: { section: this.dataset.section, price: state ? formatMoney(total) : '', label: state ? state.label : '', available },
      }));
    }

    async onSubmit(e) {
      e.preventDefault();
      const state = this.current();
      if (!state || !state.available || this.busy) return;
      this.busy = true;
      this.setBusy(true);
      this.showError('');
      const item = { id: state.variant.id, quantity: state.qty * this.multiplierValue };
      if (state.plan) item.selling_plan = state.plan.id;
      try {
        const res = await CartAPI.add([item]);
        await afterAdd(res);
      } catch (err) {
        this.showError(err.message);
      } finally {
        this.busy = false;
        this.setBusy(false);
      }
    }

    requestSubmit() {
      if (this.form?.requestSubmit) this.form.requestSubmit();
      else this.form?.dispatchEvent(new Event('submit', { cancelable: true }));
    }

    setBusy(busy) {
      [this.submit, qs(`sticky-atc[data-form-id="${this.dataset.formId}"] [data-sticky-submit]`)].forEach((b) => {
        if (!b) return;
        if (busy) b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy');
      });
    }

    showError(msg) {
      const el = qs('[data-form-error]', this);
      if (!el) { if (msg) toast(msg); return; }
      el.textContent = msg;
      el.hidden = !msg;
    }
  }
  customElements.define('product-page', ProductPage);

  /* Selector de cantidad (+/−) del formulario de producto */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.closest('cart-drawer')) return; // el cajón gestiona sus propios botones
      this.input = qs('input', this);
      qs('[data-qty-minus]', this)?.addEventListener('click', () => this.step(-1));
      qs('[data-qty-plus]', this)?.addEventListener('click', () => this.step(1));
    }
    step(d) {
      const min = Number(this.input.min) || 1;
      const max = Number(this.input.max) || 99;
      const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + d));
      if (String(next) === this.input.value) return;
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------------------------------------------------------------------- */
  /* Barra fija de compra (móvil)                                           */
  /* ---------------------------------------------------------------------- */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.page = qs(`product-page[data-form-id="${this.dataset.formId}"]`);
      this.target = this.page && qs('[data-submit]', this.page);
      if (!this.page || !this.target) return;
      this.hidden = false;
      this.drawerOpen = false;
      this.targetVisible = true;
      qs('[data-sticky-submit]', this).addEventListener('click', () => this.page.requestSubmit());
      // Comprobación ligera en scroll/resize (sin dependencias de IntersectionObserver).
      this.footer = qs('.site-footer');
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        setTimeout(() => { this.ticking = false; this.measure(); }, 80);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.measure();
      document.addEventListener('drawer:open', () => { this.drawerOpen = true; this.update(); });
      document.addEventListener('drawer:close', () => { this.drawerOpen = false; this.update(); });
      document.addEventListener('product:state', (e) => {
        if (e.detail.section !== this.page.dataset.section) return;
        qs('[data-sticky-price]', this).textContent = e.detail.price;
        qs('[data-sticky-offer]', this).textContent = e.detail.label;
        qs('[data-sticky-submit]', this).disabled = !e.detail.available;
      });
      if (this.page.product) this.page.render();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    measure() {
      const vh = window.innerHeight;
      const t = this.target.getBoundingClientRect();
      this.targetVisible = t.bottom > 0 && t.top < vh - 40;
      const f = this.footer && this.footer.getBoundingClientRect();
      this.footerVisible = Boolean(f && f.top < vh);
      this.update();
    }
    update() {
      const show = !this.targetVisible && !this.drawerOpen && !this.footerVisible;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      qsa('button', this).forEach((b) => { b.tabIndex = show ? 0 : -1; });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------------------------------------------------------------------- */
  /* Packs y ahorro                                                          */
  /* ---------------------------------------------------------------------- */

  class BundleOffers extends HTMLElement {
    connectedCallback() {
      this.product = readJSON(qs('[data-product-json]', this));
      if (!this.product) return;
      const variant = this.product.variants.find((v) => v.available) || this.product.variants[0];
      qsa('.bundle-card[data-offer]', this).forEach((card) => {
        const offer = JSON.parse(card.dataset.offer);
        const r = computeOffer(this.product, variant, offer);
        paintPrices(card, r);
        const btn = qs('[data-bundle-add]', card);
        btn.dataset.variantId = r.variant.id;
        btn.dataset.quantity = r.qty;
        btn.dataset.sellingPlan = r.plan ? r.plan.id : '';
        btn.disabled = !r.available;
        if (!r.available) qs('.button__label', btn).textContent = strings.unavailable;
      });
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-bundle-add]');
        if (btn) this.add(btn);
      });
    }

    async add(btn) {
      if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
      const err = qs('[data-form-error]', this);
      if (err) err.hidden = true;
      const item = { id: Number(btn.dataset.variantId), quantity: Number(btn.dataset.quantity) || 1 };
      if (btn.dataset.sellingPlan) item.selling_plan = Number(btn.dataset.sellingPlan);
      try {
        await afterAdd(await CartAPI.add([item]));
      } catch (e) {
        if (err) { err.textContent = e.message; err.hidden = false; } else toast(e.message);
      } finally {
        btn.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('bundle-offers', BundleOffers);

  /* ---------------------------------------------------------------------- */
  /* Carrusel genérico (reseñas, testimonios)                                */
  /* ---------------------------------------------------------------------- */

  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-track]', this);
      this.slides = qsa('.slider__slide', this);
      this.prev = qs('[data-prev]', this);
      this.next = qs('[data-next]', this);
      this.current = qs('[data-current]', this);
      const total = qs('[data-total]', this);
      if (total) total.textContent = this.slides.length;
      if (!this.track || !this.slides.length) return;
      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.update());
      }, { passive: true });
      window.addEventListener('resize', () => this.update(), { passive: true });
      this.update();
    }
    slideWidth() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return this.slides[0].getBoundingClientRect().width + gap;
    }
    step(d) { this.track.scrollBy({ left: d * this.slideWidth(), behavior: 'smooth' }); }
    update() {
      const w = this.slideWidth() || 1;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const index = Math.round(this.track.scrollLeft / w);
      if (this.current) this.current.textContent = Math.min(this.slides.length, index + 1);
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
      const controls = qs('.slider__controls', this);
      if (controls) controls.hidden = max <= 2;
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------------------------------------------------------------------- */
  /* Acordeones con animación suave                                          */
  /* ---------------------------------------------------------------------- */

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('details.accordion > summary, details.product-accordion > summary');
    if (!summary || reduceMotion.matches || !summary.parentElement.animate) return;
    e.preventDefault();
    const details = summary.parentElement;
    if (details._anim) { details._anim.onfinish = null; details._anim.cancel(); clearTimeout(details._t); }
    const start = details.offsetHeight;
    if (details.open) {
      const end = summary.offsetHeight;
      details.style.overflow = 'hidden';
      details._anim = details.animate({ height: [`${start}px`, `${end}px`] }, { duration: 280, easing: 'cubic-bezier(.22,.61,.36,1)' });
      const done = () => { clearTimeout(details._t); details.open = false; details.style.overflow = ''; details._anim = null; };
      details._anim.onfinish = done;
      details._t = setTimeout(done, 340); // por si la animación no llega a ejecutarse (pestaña en segundo plano)
    } else {
      details.open = true;
      const end = details.offsetHeight;
      details.style.overflow = 'hidden';
      details._anim = details.animate({ height: [`${start}px`, `${end}px`] }, { duration: 320, easing: 'cubic-bezier(.22,.61,.36,1)' });
      details._anim.onfinish = () => { details.style.overflow = ''; details._anim = null; };
    }
  });

  /* ---------------------------------------------------------------------- */
  /* Animaciones de aparición                                               */
  /* ---------------------------------------------------------------------- */

  function initReveal(root = document) {
    const els = qsa('.reveal:not(.is-visible)', root);
    if (!('IntersectionObserver' in window) || window.Shopify?.designMode) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((el) => io.observe(el));
  }

  document.addEventListener('DOMContentLoaded', () => initReveal());
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

  // Editor de temas: selecciona la opción de compra al hacer clic en su bloque.
  document.addEventListener('shopify:block:select', (e) => {
    const radio = e.target.querySelector?.('.offer__radio');
    if (radio && !radio.disabled) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
    const details = e.target.closest?.('details') || (e.target.matches?.('details') ? e.target : null);
    if (details) details.open = true;
  });

  window.themeUtils = { formatMoney, computeOffer, CartAPI };
})();
